import { PAYROLL_FIELD_TIP_ID, PAYROLL_FIELD_TRAVEL_ID } from '@ubeya/shared/constants';
import { KAPAIM_ACCOUNT_ID } from '@ubeya/shared/utils/accounts';

export const BASE_PRICE = 32.31;

export const accountsWithSystemExport = {
  235: true,
  1: true,
  261: true,
  8: true,
  223: true,
  144: true,
  153: true,
  2080: true,
  4888: true,
  12510: true,
  12720: true,
  22859: true,
  3207: true,
  [KAPAIM_ACCOUNT_ID]: true
};

export const MICHPAL = {
  235: { companyNumber: 2 },
  1: { companyNumber: '' },
  261: { companyNumber: 2 },
  153: { companyNumber: '' },
  22859: { companyNumber: 2363331 },
  [KAPAIM_ACCOUNT_ID]: { companyNumber: 301 }
};

const MINIMUM_HOURLY_RATE = 34.32;
const MINIMUM_HOURLY_RATE_12720 = 35.4;

const sumAggregatedHours = (aggregatedHours = {}) =>
  (aggregatedHours.regular || 0) +
  (aggregatedHours.overtime || 0) +
  (aggregatedHours.overtime2 || 0) +
  (aggregatedHours.holidayRegular || 0) +
  (aggregatedHours.holidayOvertime || 0) +
  (aggregatedHours.holidayOvertime2 || 0);

// Weighted-average hourly wage across all shifts (weighted by worked hours).
// Used as a fallback when an employee has multiple shifts at different hourly rates,
// so that rate × total hours still produces the correct gross amount.
const getWeightedAvgHourlyWage = (subRows = []) => {
  let totalHours = 0;
  let totalWage = 0;

  subRows.forEach((row) => {
    const rate = row?.wage?.hourlyWage ?? MINIMUM_HOURLY_RATE;
    const hours = sumAggregatedHours(row?.payroll?.aggregatedHours);
    totalHours += hours;
    totalWage += rate * hours;
  });

  if (totalHours === 0) {
    return subRows[0]?.wage?.hourlyWage ?? MINIMUM_HOURLY_RATE;
  }

  return totalWage / totalHours;
};

export const MEGA_PAY_CODES = {
  22393: [
    {
      type: 'A',
      code: 'A01',
      aggregationCode: 'A-payroll-value',
      value: (payroll) => {
        if (!payroll) {
          return 0;
        }

        const totalPayrollFields = (payroll?.fields || []).reduce((total, curr) => total + curr.value || 0, 0) || 0;
        return payroll.value - totalPayrollFields;
      },
      payRate: (payroll) => payroll?.wage?.hourlyWage || 0
    },
    {
      type: 'A',
      code: 'A03',
      aggregationCode: 'A-travel',
      value: ({ fields = [] } = {}) => fields.find(({ id }) => id === 2)?.value || 0,
      payRate: (payroll) => payroll?.wage?.hourlyWage || 0
    },
    {
      type: 'A',
      code: 'A06',
      aggregationCode: 'A-weekly-pay',
      value: ({ fields = [] } = {}) => fields.find(({ id }) => id === 458)?.value || 0,
      payRate: (payroll) => payroll?.wage?.hourlyWage || 0
    },
    {
      type: 'H',
      code: 'H01',
      aggregationCode: 'H-regular',
      value: ({ aggregatedHours = {} } = {}) => aggregatedHours.regular
    },
    {
      type: 'H',
      code: 'H01',
      aggregationCode: 'H-overtime',
      value: ({ aggregatedHours = {} } = {}) => aggregatedHours.overtime
    },
    {
      type: 'H',
      code: 'H01',
      aggregationCode: 'H-overtime2',
      value: ({ aggregatedHours = {} } = {}) => aggregatedHours.overtime2
    }
  ]
};

/** Must stay aligned with `EXTRA_PAYROLL_COLUMNS_WITH_HOLIDAY` in payroll.js (Friends / 12510 and account 1). */
const FRIENDS_12510_SHIKLULIT_AGGREGATED_HOUR_KEYS = [
  'regular',
  'overtime',
  'overtime2',
  'holidayRegular',
  'holidayOvertime',
  'holidayOvertime2'
];

const getFriends12510WageBucketKey = (subRow) => {
  const wage = subRow.wage || subRow.payroll?.wage;
  const hourlyWage = wage?.hourlyWage;
  // Prefer numeric rate so duplicate wage rows with the same hourlyWage share one bucket.
  if (hourlyWage !== null && hourlyWage !== undefined && !Number.isNaN(Number(hourlyWage))) {
    return `hourlyWage:${String(Math.round(Number(hourlyWage) * 10000) / 10000)}`;
  }
  if (wage?.id) {
    return `wageId:${wage.id}`;
  }
  if (wage?.flatWage) {
    return `flatWage:${wage.flatWage}`;
  }
  return 'default';
};

export const buildFriendsShiklulitRowContextsForEmployee = (item) => {
  if (!item) {
    return [];
  }
  const { subRows = [] } = item;
  if (subRows.length === 0) {
    return [item];
  }

  const byKey = new Map();
  for (const subRow of subRows) {
    const key = getFriends12510WageBucketKey(subRow);
    if (!byKey.has(key)) {
      byKey.set(key, []);
    }
    byKey.get(key).push(subRow);
  }

  if (byKey.size <= 1) {
    return [item];
  }

  return Array.from(byKey.values()).map((bucketSubRows) => ({
    ...item,
    subRows: bucketSubRows,
    aggregatedHoursTotalRow: FRIENDS_12510_SHIKLULIT_AGGREGATED_HOUR_KEYS.reduce(
      (prev, col) => ({
        ...prev,
        [col]: bucketSubRows.reduce((sum, subRow) => sum + (subRow?.payroll?.aggregatedHours?.[col] || 0), 0)
      }),
      {}
    ),
    wage: bucketSubRows[0]?.wage,
    payroll: bucketSubRows[0]?.payroll
  }));
};

export const buildFriendsShiklulitDataRows = ({ item, rowContexts, accountId }) => {
  const rows = [];

  (SHIKLULIT[accountId] || []).forEach(({ type, code, price, qty, source }) => {
    if (source !== 'hourlyRate') {
      const priceColumn = typeof price === 'function' ? price(item, []) : price;
      const qtyColumn = typeof qty === 'function' ? qty(item) : qty;
      rows.push({ type, code, priceColumn, qtyColumn });
      return;
    }

    const rowsByHourlyRate = new Map();
    rowContexts.forEach((rowItem) => {
      const priceColumn = typeof price === 'function' ? price(rowItem, []) : price;
      const qtyColumn = typeof qty === 'function' ? qty(rowItem) : qty;
      const hourlyRateKey = String(priceColumn);
      const existing = rowsByHourlyRate.get(hourlyRateKey);
      if (existing) {
        existing.qtyColumn += qtyColumn || 0;
      } else {
        rowsByHourlyRate.set(hourlyRateKey, { type, code, priceColumn, qtyColumn: qtyColumn || 0 });
      }
    });

    rowsByHourlyRate.forEach((row) => rows.push(row));
  });

  return rows;
};

const SHIKLULIT_FRIENDS_12510_ROWS = [
  {
    type: 4,
    code: 5,
    price: 1,
    qty: ({ aggregatedHoursTotalRow }) =>
      aggregatedHoursTotalRow.regular +
      aggregatedHoursTotalRow.overtime +
      aggregatedHoursTotalRow.overtime2 +
      aggregatedHoursTotalRow.holidayRegular +
      aggregatedHoursTotalRow.holidayOvertime +
      aggregatedHoursTotalRow.holidayOvertime2
  },
  { type: 4, code: 7, price: 1, qty: ({ subRows }) => subRows.length },
  { type: 4, code: 4, price: 1, qty: ({ subRows }) => subRows.length },

  //שכר בסיס
  {
    type: 1,
    code: 1,
    source: 'hourlyRate',
    price: (item) => item?.subRows?.[0]?.wage?.hourlyWage ?? MINIMUM_HOURLY_RATE,
    qty: ({ aggregatedHoursTotalRow }) => aggregatedHoursTotalRow.regular
  },

  // נסיעות
  {
    type: 1,
    code: 3,
    price: (item) =>
      (item?.subRows || []).reduce((output, current) => {
        const field2 = current?.payroll?.fields?.find(({ id }) => id === 2)?.value || 0;
        const field194 = current?.payroll?.fields?.find(({ id }) => id === 194)?.value || 0;
        return output + field2 + field194;
      }, 0),
    qty: 1
  },

  //  שעות נוספות 1 - 125%
  {
    type: 1,
    code: 834,
    source: 'hourlyRate',
    price: (item) => (item?.subRows?.[0]?.wage?.hourlyWage ?? MINIMUM_HOURLY_RATE) * 1.25,
    qty: ({ aggregatedHoursTotalRow }) => aggregatedHoursTotalRow.overtime
  },

  //  שעות נוספות 2 - 150%
  {
    type: 1,
    code: 835,
    source: 'hourlyRate',
    price: (item) => (item?.subRows?.[0]?.wage?.hourlyWage ?? MINIMUM_HOURLY_RATE) * 1.5,
    qty: ({ aggregatedHoursTotalRow }) => aggregatedHoursTotalRow.overtime2
  },

  // טיפ
  {
    type: 1,
    code: 182,
    price: (item) =>
      (item?.subRows || []).reduce(
        (output, current) => output + (current?.payroll?.fields?.find(({ id }) => id === 1)?.value || 0),
        0
      ),
    qty: 1
  },

  //  שעות שבת  - 150%
  {
    type: 1,
    code: 836,
    source: 'hourlyRate',
    price: (item) => (item?.subRows?.[0]?.wage?.hourlyWage ?? MINIMUM_HOURLY_RATE) * 1.5,
    qty: ({ aggregatedHoursTotalRow }) => aggregatedHoursTotalRow.holidayRegular
  },

  //  שעות שבת נוספות 1 - 175%
  {
    type: 1,
    code: 171,
    source: 'hourlyRate',
    price: (item) => (item?.subRows?.[0]?.wage?.hourlyWage ?? MINIMUM_HOURLY_RATE) * 1.75,
    qty: ({ aggregatedHoursTotalRow }) => aggregatedHoursTotalRow.holidayOvertime
  },

  //  שעות שבת נוספות 2 - 200%
  {
    type: 1,
    code: 167,
    source: 'hourlyRate',
    price: (item) => (item?.subRows?.[0]?.wage?.hourlyWage ?? MINIMUM_HOURLY_RATE) * 2,
    qty: ({ aggregatedHoursTotalRow }) => aggregatedHoursTotalRow.holidayOvertime2
  },
  {
    type: 1,
    code: 1500,
    price: 0,
    qty: 0
  },
  {
    type: 1,
    code: 831,
    price: 0,
    qty: 0
  },
  {
    type: 1,
    code: 833,
    price: 0,
    qty: 0
  },
  {
    type: 1,
    code: 900,
    price: 0,
    qty: 0
  },
  {
    type: 1,
    code: 901,
    price: 0,
    qty: 0
  },
  {
    type: 1,
    code: 902,
    price: 0,
    qty: 0
  },
  {
    type: 1,
    code: 903,
    price: 0,
    qty: 0
  },
  {
    type: 1,
    code: 904,
    price: 0,
    qty: 0
  },
  {
    type: 1,
    code: 905,
    price: 0,
    qty: 0
  },

  {
    type: 1,
    code: 906,
    price: 0,
    qty: 0
  },

  {
    type: 1,
    code: 907,
    price: 0,
    qty: 0
  },

  {
    type: 1,
    code: 908,
    price: 0,
    qty: 0
  },

  {
    type: 1,
    code: 909,
    price: 0,
    qty: 0
  },

  {
    type: 1,
    code: 910,
    price: 0,
    qty: 0
  },
  {
    type: 1,
    code: 911,
    price: 0,
    qty: 0
  },

  {
    type: 1,
    code: 913,
    price: 0,
    qty: 0
  },
  {
    type: 1,
    code: 164,
    price: 0,
    qty: 0
  },
  {
    type: 1,
    code: 165,
    price: 0,
    qty: 0
  },

  {
    type: 1,
    code: 914,
    price: 0,
    qty: 0
  },
  {
    type: 1,
    code: 2001,
    price: 0,
    qty: 0
  },
  // השלמת שכר
  {
    type: 1,
    code: 2004,
    price: (item) =>
      (item?.subRows || []).reduce(
        (output, current) => output + (current?.payroll?.fields?.find(({ id }) => id === 195)?.value || 0),
        0
      ),
    qty: 1
  },
  {
    type: 1,
    code: 119,
    price: 0,
    qty: 0
  }
];

export const SHIKLULIT = {
  8: [
    { type: 4, code: 5, price: 1, qty: ({ actualTotal }) => actualTotal / 1000 / 60 / 60 },
    { type: 4, code: 7, price: 1, qty: ({ subRows }) => subRows.length },
    { type: 4, code: 4, price: 1, qty: ({ subRows }) => subRows.length },

    {
      type: 1,
      code: 1,
      price: BASE_PRICE,
      qty: ({ aggregatedHoursTotalRow }) => aggregatedHoursTotalRow.regular
    },
    { type: 1, code: 3, price: ({ fields }) => fields[1], qty: 1 },
    { type: 1, code: 4, price: 1.5, qty: ({ fields }) => fields[2] },
    {
      type: 1,
      code: 834,
      price: BASE_PRICE * 1.25,
      qty: ({ aggregatedHoursTotalRow }) => aggregatedHoursTotalRow.overtime
    },
    {
      type: 1,
      code: 835,
      price: BASE_PRICE * 1.5,
      qty: ({ aggregatedHoursTotalRow }) => aggregatedHoursTotalRow.overtime2
    },
    { type: 1, code: 167, price: 0, qty: 0 },
    {
      type: 1,
      code: 171,
      price: BASE_PRICE * 1.75,
      qty: ({ aggregatedHoursTotalRow }) => aggregatedHoursTotalRow.holidayOvertime
    },
    {
      type: 1,
      code: 836,
      price: BASE_PRICE * 1.5,
      qty: ({ aggregatedHoursTotalRow }) => aggregatedHoursTotalRow.holidayRegular
    },
    {
      type: 1,
      code: 832,
      price: ({ payrollTotal }, allDataBefore) =>
        payrollTotal - allDataBefore.reduce((sum, { price, qty }) => sum + (price || 0) * (qty || 0), 0),
      qty: 1
    },
    { type: 1, code: 1500, price: 0, qty: 0 },
    { type: 1, code: 831, price: 0, qty: 0 },
    { type: 1, code: 833, price: 0, qty: 0 },
    { type: 1, code: 900, price: 0, qty: 0 },
    { type: 1, code: 901, price: 0, qty: 0 },
    { type: 1, code: 902, price: 0, qty: 0 },
    { type: 1, code: 903, price: 0, qty: 0 },
    { type: 1, code: 904, price: 0, qty: 0 },
    { type: 1, code: 905, price: 0, qty: 0 },
    { type: 1, code: 906, price: 0, qty: 0 },
    { type: 1, code: 907, price: 0, qty: 0 },
    { type: 1, code: 908, price: 0, qty: 0 },
    { type: 1, code: 909, price: 0, qty: 0 },
    { type: 1, code: 910, price: 0, qty: 0 },
    { type: 1, code: 911, price: 0, qty: 0 },
    { type: 1, code: 913, price: 0, qty: 0 },
    { type: 1, code: 164, price: 0, qty: 0 },
    { type: 1, code: 165, price: 0, qty: 0 },
    { type: 1, code: 914, price: 0, qty: 0 }
  ],
  223: [
    {
      type: 1,
      code: 1,
      price: (item) => item?.subRows?.[0]?.payroll?.wage?.hourlyWage || 0,
      qty: ({ aggregatedHoursTotalRow }) => aggregatedHoursTotalRow.regular
    },
    {
      type: 1,
      code: 31,
      price: (item) => (item?.subRows?.[0]?.payroll?.wage?.hourlyWage || 0) * 1.25,
      qty: ({ aggregatedHoursTotalRow }) => aggregatedHoursTotalRow.overtime
    },
    {
      type: 1,
      code: 32,
      price: (item) => (item?.subRows?.[0]?.payroll?.wage?.hourlyWage || 0) * 1.5,
      qty: ({ aggregatedHoursTotalRow }) => aggregatedHoursTotalRow.overtime2
    },
    {
      type: 1,
      code: 3,
      price: ({ fields }) => fields[2],
      qty: 1
    },
    {
      type: 1,
      code: 600,
      price: (item) => item?.subRows?.[0]?.payroll?.wage?.flatWage || 0,
      qty: ({ subRows }) => subRows.filter((item) => item?.payroll?.wage?.flatWage > 0).length
    },
    { type: 4, code: 4, price: 1, qty: ({ subRows }) => subRows.length },
    {
      type: 4,
      code: 5,
      price: 1,
      qty: ({ aggregatedHoursTotalRow }) =>
        Object.keys(aggregatedHoursTotalRow).reduce((carry, key) => carry + aggregatedHoursTotalRow?.[key] || 0, 0)
    }
  ],
  2080: [
    {
      type: 4,
      code: 5,
      price: 1,
      qty: ({ aggregatedHoursTotalRow }) =>
        aggregatedHoursTotalRow.regular +
        aggregatedHoursTotalRow.overtime +
        aggregatedHoursTotalRow.overtime2 +
        aggregatedHoursTotalRow.holidayRegular +
        aggregatedHoursTotalRow.holidayOvertime +
        aggregatedHoursTotalRow.holidayOvertime2
    },
    {
      type: 4,
      code: 8,
      price: 1,
      qty: ({ aggregatedHoursTotalRow }) =>
        aggregatedHoursTotalRow.regular +
        aggregatedHoursTotalRow.overtime +
        aggregatedHoursTotalRow.overtime2 +
        aggregatedHoursTotalRow.holidayRegular +
        aggregatedHoursTotalRow.holidayOvertime +
        aggregatedHoursTotalRow.holidayOvertime2
    },
    { type: 4, code: 7, price: 1, qty: ({ subRows }) => subRows.length },
    { type: 4, code: 4, price: 1, qty: ({ subRows }) => subRows.length },

    //שכר בסיס
    {
      type: 1,
      code: 1,
      price: (item) => item?.subRows?.[0]?.payroll?.wage?.hourlyWage || 0,
      qty: ({ aggregatedHoursTotalRow }) => aggregatedHoursTotalRow.regular
    },

    // נסיעות
    {
      type: 1,
      code: 3,
      price: (item) => item?.subRows?.[0]?.payroll?.fields?.find(({ id }) => id === 145)?.value,
      qty: ({ aggregatedHoursTotalRow }) => aggregatedHoursTotalRow.regular
    },

    // הבראה
    {
      type: 1,
      code: 4,
      price: (item) => item?.subRows?.[0]?.payroll?.fields?.find(({ id }) => id === 147)?.value,
      qty: ({ aggregatedHoursTotalRow }) => aggregatedHoursTotalRow.regular
    },

    // חופשה
    {
      type: 1,
      code: 8,
      price: (item) => item?.subRows?.[0]?.payroll?.fields?.find(({ id }) => id === 144)?.value,
      qty: ({ aggregatedHoursTotalRow }) => aggregatedHoursTotalRow.regular
    },

    // חגים
    {
      type: 1,
      code: 112,
      price: (item) => item?.subRows?.[0]?.payroll?.fields?.find(({ id }) => id === 146)?.value,
      qty: ({ aggregatedHoursTotalRow }) => aggregatedHoursTotalRow.regular
    },

    // תוספת לילה
    {
      type: 1,
      code: 124,
      price: (item) => item?.subRows?.[0]?.payroll?.fields?.find(({ id }) => id === 148)?.value,
      qty: ({ aggregatedHoursTotalRow }) => aggregatedHoursTotalRow.regular
    },

    // שעות נוספות 1
    {
      type: 1,
      code: 31,
      price: (item) => (item?.subRows?.[0]?.payroll?.wage?.hourlyWage || 0) * 1.25,
      qty: ({ aggregatedHoursTotalRow }) => aggregatedHoursTotalRow.overtime
    },

    // שעות נוספות 2
    {
      type: 1,
      code: 37,
      price: (item) => (item?.subRows?.[0]?.payroll?.wage?.hourlyWage || 0) * 1.5,
      qty: ({ aggregatedHoursTotalRow }) =>
        aggregatedHoursTotalRow.overtime2 +
        aggregatedHoursTotalRow.holidayRegular +
        aggregatedHoursTotalRow.holidayOvertime +
        aggregatedHoursTotalRow.holidayOvertime2
    },

    // בונוס הסעת עובדים
    {
      type: 1,
      code: 121,
      price: (item) =>
        (item?.subRows || []).reduce(
          (output, current) => output + (current?.payroll?.fields?.find(({ id }) => id === 43)?.value || 0),
          0
        ),
      qty: 1
    },

    // בונוס כללי
    {
      type: 1,
      code: 120,
      price: (item) =>
        (item?.subRows || []).reduce(
          (output, current) => output + (current?.payroll?.fields?.find(({ id }) => id === 152)?.value || 0),
          0
        ),
      qty: 1
    },

    // שווי ארוחות
    {
      type: 2,
      code: 37,
      price: 20,
      qty: (item) => item?.subRows?.length
    },

    // תוספת נסיעות
    {
      type: 1,
      code: 119,
      price: (item) => item?.subRows?.[0]?.payroll?.fields?.find(({ id }) => id === 150)?.value,
      qty: (item) => item?.subRows?.length
    }
  ],
  144: [
    {
      type: 1,
      code: 1,
      price: (item) => item?.subRows?.[0]?.payroll?.wage?.hourlyWage || 0,
      qty: ({ aggregatedHoursTotalRow }) => aggregatedHoursTotalRow.regular
    },
    {
      type: 1,
      code: 115,
      price: (item) => (item?.subRows?.[0]?.payroll?.wage?.hourlyWage || 0) * 1.25,
      qty: ({ aggregatedHoursTotalRow }) => aggregatedHoursTotalRow.overtime
    },
    {
      type: 1,
      code: 114,
      price: (item) =>
        (item?.subRows || []).reduce(
          (output, current) => output + (current?.payroll?.fields?.find(({ id }) => id === 60)?.value || 0),
          0
        ),
      qty: 1
    },
    // שעות נוספות 2 של 150%
    {
      type: 1,
      code: 116,
      price: (item) => (item?.subRows?.[0]?.payroll?.wage?.hourlyWage || 0) * 1.5,
      qty: ({ aggregatedHoursTotalRow }) => aggregatedHoursTotalRow.overtime2
    },
    //שעות שבת של 150%
    {
      type: 1,
      code: 139,
      price: (item) => (item?.subRows?.[0]?.payroll?.wage?.hourlyWage || 0) * 1.5,
      qty: ({ subRows }) =>
        subRows.reduce((all, currRow) => {
          const { aggregatedHours } = currRow.payroll || {};

          let result = 0;
          Object.entries(currRow?.multipliers || {}).forEach(([hourType, multiplier]) => {
            if (['holidayRegular', 'holidayOvertime', 'holidayOvertime2'].includes(hourType) && multiplier === 1.5) {
              result += aggregatedHours?.[hourType] || 0;
            }
          });

          return all + result;
        }, 0)
    },
    {
      type: 1,
      code: 200,
      price: (item) => (item?.subRows?.[0]?.payroll?.wage?.hourlyWage || 0) * 1.75,
      qty: ({ subRows }) =>
        subRows.reduce((all, currRow) => {
          const { aggregatedHours } = currRow.payroll || {};

          let result = 0;
          Object.entries(currRow?.multipliers || {}).forEach(([hourType, multiplier]) => {
            if (['holidayRegular', 'holidayOvertime', 'holidayOvertime2'].includes(hourType) && multiplier === 1.75) {
              result += aggregatedHours?.[hourType] || 0;
            }
          });

          return all + result;
        }, 0)
    },
    {
      type: 1,
      code: 208,
      price: (item) => (item?.subRows?.[0]?.payroll?.wage?.hourlyWage || 0) * 2,
      qty: ({ subRows }) =>
        (subRows || []).reduce((all, currRow) => {
          const { aggregatedHours } = currRow.payroll || {};

          let result = 0;
          Object.entries(currRow?.multipliers || {}).forEach(([hourType, multiplier]) => {
            if (['holidayRegular', 'holidayOvertime', 'holidayOvertime2'].includes(hourType) && multiplier === 2) {
              result += aggregatedHours?.[hourType] || 0;
            }
          });

          return all + result;
        }, 0)
    },
    {
      type: 1,
      code: 112,
      price: (item) =>
        (item?.subRows || []).reduce(
          (output, current) => output + (current?.payroll?.fields?.find(({ id }) => id === 1)?.value || 0),
          0
        ),
      qty: 1
    },
    {
      type: 1,
      code: 3,
      price: ({ fields }) => fields[2],
      qty: 1
    },
    {
      type: 1,
      code: 114,
      price: ({ fields }) => fields[1],
      qty: 1
    },
    { type: 4, code: 4, price: 1, qty: ({ subRows }) => subRows.length },
    {
      type: 4,
      code: 5,
      price: 1,
      qty: ({ aggregatedHoursTotalRow }) =>
        Object.keys(aggregatedHoursTotalRow).reduce((carry, key) => carry + aggregatedHoursTotalRow?.[key] || 0, 0)
    },
    // שווי ארוחות
    {
      type: 2,
      code: 21,
      price: (item) =>
        (item?.subRows || []).reduce(
          (output, current) => output + (current?.payroll?.fields?.find(({ id }) => id === 1140)?.value || 0),
          0
        ),
      qty: 1
    }
  ],
  4888: [
    {
      type: 4,
      code: 5,
      price: 1,
      qty: ({ aggregatedHoursTotalRow }) =>
        aggregatedHoursTotalRow.regular +
        aggregatedHoursTotalRow.overtime +
        aggregatedHoursTotalRow.overtime2 +
        aggregatedHoursTotalRow.holidayRegular +
        aggregatedHoursTotalRow.holidayOvertime +
        aggregatedHoursTotalRow.holidayOvertime2
    },
    { type: 4, code: 7, price: 1, qty: ({ subRows }) => subRows.length },
    { type: 4, code: 4, price: 1, qty: ({ subRows }) => subRows.length },

    //שכר בסיס
    {
      type: 1,
      code: 1,
      price: (item) => item?.subRows?.[0]?.payroll?.wage?.hourlyWage || 0,
      qty: ({ aggregatedHoursTotalRow }) => aggregatedHoursTotalRow.regular
    },

    // שעות נוספות 2 עם 150%
    {
      type: 1,
      code: 33,
      price: (item) => (item?.subRows?.[0]?.payroll?.wage?.hourlyWage || 0) * 1.5,
      qty: ({ aggregatedHoursTotalRow }) => aggregatedHoursTotalRow.overtime + aggregatedHoursTotalRow.overtime2
    },

    // נסיעות
    {
      type: 1,
      code: 3,
      price: (item) =>
        (item?.subRows || []).reduce(
          (output, current) => output + (current?.payroll?.fields?.find(({ id }) => id === 138)?.value || 0),
          0
        ),
      qty: 1
    },

    //  שעות שבת 150%
    {
      type: 1,
      code: 551,
      price: (item) => (item?.subRows?.[0]?.payroll?.wage?.hourlyWage || 0) * 1.5,
      qty: ({ aggregatedHoursTotalRow }) => aggregatedHoursTotalRow.holidayRegular
    },

    //  שעות שבת 2 של 150%
    {
      type: 1,
      code: 31,
      price: (item) => (item?.subRows?.[0]?.payroll?.wage?.hourlyWage || 0) * 2,
      qty: ({ aggregatedHoursTotalRow }) =>
        aggregatedHoursTotalRow.holidayOvertime + aggregatedHoursTotalRow.holidayOvertime2
    },

    // בונוס שעות נוספות

    {
      type: 1,
      code: 44,
      price: (item) =>
        (item?.subRows || []).reduce(
          (output, current) => output + (current?.payroll?.fields?.find(({ id }) => id === 186)?.value || 0),
          0
        ),
      qty: 1
    },

    // בונוס כללי
    {
      type: 1,
      code: 125,
      price: (item) =>
        (item?.subRows || []).reduce(
          (output, current) => output + (current?.payroll?.fields?.find(({ id }) => id === 218)?.value || 0),
          0
        ),
      qty: 1
    },

    // בונוס קיץ
    {
      type: 1,
      code: 555,
      price: (item) =>
        (item?.subRows || []).reduce(
          (output, current) => output + (current?.payroll?.fields?.find(({ id }) => id === 183)?.value || 0),
          0
        ),
      qty: 1
    },

    // בונוס סידור עבודה

    {
      type: 1,
      code: 524,
      price: (item) =>
        (item?.subRows || []).reduce(
          (output, current) => output + (current?.payroll?.fields?.find(({ id }) => id === 181)?.value || 0),
          0
        ),
      qty: 1
    },

    //בונוס אירועים
    {
      type: 1,
      code: 554,
      price: (item) =>
        (item?.subRows || []).reduce(
          (output, current) => output + (current?.payroll?.fields?.find(({ id }) => id === 182)?.value || 0),
          0
        ),
      qty: 1
    },

    //בונוס טיפ
    {
      type: 1,
      code: 556,
      price: (item) =>
        (item?.subRows || []).reduce(
          (output, current) => output + (current?.payroll?.fields?.find(({ id }) => id === 1)?.value || 0),
          0
        ),
      qty: 1
    }
  ],

  12720: [
    {
      type: 4,
      code: 5,
      price: 1,
      qty: ({ aggregatedHoursTotalRow }) =>
        aggregatedHoursTotalRow.regular +
        aggregatedHoursTotalRow.overtime +
        aggregatedHoursTotalRow.overtime2 +
        aggregatedHoursTotalRow.holidayRegular +
        aggregatedHoursTotalRow.holidayOvertime +
        aggregatedHoursTotalRow.holidayOvertime2
    },
    { type: 4, code: 7, price: 1, qty: ({ subRows }) => subRows.length },
    { type: 4, code: 4, price: 1, qty: ({ subRows }) => subRows.length },

    //שכר בסיס
    {
      type: 1,
      code: 1,
      price: MINIMUM_HOURLY_RATE_12720,
      qty: ({ aggregatedHoursTotalRow }) => aggregatedHoursTotalRow.regular
    },

    // נסיעות
    {
      type: 1,
      code: 3,
      price: 30,
      qty: ({ subRows }) => subRows.length
    },

    //  שעות נוספות 1 - 125%
    {
      type: 1,
      code: 190,
      price: MINIMUM_HOURLY_RATE_12720 * 1.25,
      qty: ({ aggregatedHoursTotalRow }) => aggregatedHoursTotalRow.overtime
    },

    //  שעות נוספות 2 + שעות שבת רגילות - 150%
    {
      type: 1,
      code: 191,
      price: MINIMUM_HOURLY_RATE_12720 * 1.5,
      qty: ({ aggregatedHoursTotalRow }) => aggregatedHoursTotalRow.overtime2 + aggregatedHoursTotalRow.holidayRegular
    },

    //  שעות שבת נוספות 1 - 175%
    {
      type: 1,
      code: 291,
      price: MINIMUM_HOURLY_RATE_12720 * 1.75,
      qty: ({ aggregatedHoursTotalRow }) => aggregatedHoursTotalRow.holidayOvertime
    },

    //  שעות שבת נוספות 2 - 200%
    {
      type: 1,
      code: 195,
      price: MINIMUM_HOURLY_RATE_12720 * 2,
      qty: ({ aggregatedHoursTotalRow }) => aggregatedHoursTotalRow.holidayOvertime2
    },

    // פרמיה
    {
      type: 1,
      code: 407,
      price: ({ payrollTotal, subRows, aggregatedHoursTotalRow }) =>
        payrollTotal
          ? payrollTotal -
            (MINIMUM_HOURLY_RATE_12720 * aggregatedHoursTotalRow.regular +
              MINIMUM_HOURLY_RATE_12720 * 1.25 * aggregatedHoursTotalRow.overtime +
              MINIMUM_HOURLY_RATE_12720 * 1.5 * aggregatedHoursTotalRow.overtime2 +
              MINIMUM_HOURLY_RATE_12720 * 1.5 * aggregatedHoursTotalRow.holidayRegular +
              MINIMUM_HOURLY_RATE_12720 * 1.75 * aggregatedHoursTotalRow.holidayOvertime +
              MINIMUM_HOURLY_RATE_12720 * 2 * aggregatedHoursTotalRow.holidayOvertime2 +
              MINIMUM_HOURLY_RATE_12720 * subRows.length)
          : 0,
      qty: 1
    }
  ],

  1: SHIKLULIT_FRIENDS_12510_ROWS,

  12510: SHIKLULIT_FRIENDS_12510_ROWS,

  // Concept Group
  3207: [
    {
      type: 4,
      code: 5,
      price: 1,
      qty: ({ aggregatedHoursTotalRow }) =>
        aggregatedHoursTotalRow.regular +
        aggregatedHoursTotalRow.overtime +
        aggregatedHoursTotalRow.overtime2 +
        aggregatedHoursTotalRow.holidayRegular +
        aggregatedHoursTotalRow.holidayOvertime +
        aggregatedHoursTotalRow.holidayOvertime2
    },
    { type: 4, code: 7, price: 1, qty: ({ subRows }) => subRows.length },
    { type: 4, code: 4, price: 1, qty: ({ subRows }) => subRows.length },

    // שכר יסוד
    {
      type: 1,
      code: 1,
      price: (item) => getWeightedAvgHourlyWage(item?.subRows),
      qty: ({ aggregatedHoursTotalRow }) => aggregatedHoursTotalRow.regular
    },

    // נסיעות
    {
      type: 1,
      code: 3,
      price: (item) =>
        (item?.subRows || []).reduce(
          (output, current) => output + (current?.payroll?.fields?.find(({ id }) => id === 2)?.value || 0),
          0
        ),
      qty: 1
    },

    // יום בחירות
    {
      type: 1,
      code: 31,
      price: (item) =>
        (item?.subRows || []).reduce(
          (output, current) => output + (current?.payroll?.fields?.find(({ id }) => id === 1198)?.value || 0),
          0
        ),
      qty: 1
    },

    // שעות נוספות 125%
    {
      type: 1,
      code: 35,
      price: (item) => getWeightedAvgHourlyWage(item?.subRows) * 1.25,
      qty: ({ aggregatedHoursTotalRow }) => aggregatedHoursTotalRow.overtime
    },

    // שעות נוספות 150%
    {
      type: 1,
      code: 36,
      price: (item) => getWeightedAvgHourlyWage(item?.subRows) * 1.5,
      qty: ({ aggregatedHoursTotalRow }) => aggregatedHoursTotalRow.overtime2
    },

    // שעות שבת רגילות
    {
      type: 1,
      code: 37,
      price: (item) => getWeightedAvgHourlyWage(item?.subRows) * 1.5,
      qty: ({ aggregatedHoursTotalRow }) => aggregatedHoursTotalRow.holidayRegular
    },

    // שעות שבת 125% (175% effective)
    {
      type: 1,
      code: 38,
      price: (item) => getWeightedAvgHourlyWage(item?.subRows) * 1.75,
      qty: ({ aggregatedHoursTotalRow }) => aggregatedHoursTotalRow.holidayOvertime
    },

    // שעות שבת 150% (200% effective)
    {
      type: 1,
      code: 39,
      price: (item) => getWeightedAvgHourlyWage(item?.subRows) * 2,
      qty: ({ aggregatedHoursTotalRow }) => aggregatedHoursTotalRow.holidayOvertime2
    },

    // בונוס
    {
      type: 1,
      code: 92,
      price: (item) =>
        (item?.subRows || []).reduce(
          (output, current) => output + (current?.payroll?.fields?.find(({ id }) => id === 1199)?.value || 0),
          0
        ),
      qty: 1
    },

    // תמריץ - hours-based payroll field: value × hours per shift, summed across shifts
    {
      type: 1,
      code: 1000,
      price: (item) =>
        (item?.subRows || []).reduce((output, current) => {
          const fieldValue = current?.payroll?.fields?.find(({ id }) => id === 58)?.value || 0;
          const totalHours = sumAggregatedHours(current?.payroll?.aggregatedHours);
          return output + fieldValue * totalHours;
        }, 0),
      qty: 1
    }
  ]
};

export const TEMP_STAFF_PAYROLL_REPORT_CODES = [
  { code: 1100, column: 'SL "Service 26"', rate: 26, isHoursRelated: true },
  { code: 1102, column: 'SL "Grundlohn"', rate: 25, isHoursRelated: true },
  { code: 1105, column: 'SL "Service 27"', rate: 27, isHoursRelated: true },
  { code: 1108, column: 'SL "Chef de Service 35"', rate: 35, isHoursRelated: true },
  { code: 1110, column: 'SL "Chef de Service 30"', rate: 30, isHoursRelated: true },
  { code: 1116, column: 'SL "Service 28"', rate: 28, isHoursRelated: true },
  { code: 1117, column: 'SL "Service 40"', rate: 40, isHoursRelated: true },
  { code: 1118, column: 'SL "Service 45"', rate: 45, isHoursRelated: true },
  {
    code: 1130,
    column: 'SL "Service 26" Zuschlag 25%',
    rate: 32.5,
    isHoursRelated: true,
    isOverTime: true
  },
  {
    code: 1132,
    column: 'SL "Grundlohn" Zuschlag 25%',
    rate: 31.25,
    isHoursRelated: true,
    isOverTime: true
  },
  {
    code: 1135,
    column: 'SL "gBR-Lohn 27" Zuschlag 25%',
    rate: 33.75,
    isHoursRelated: true,
    isOverTime: true
  },
  {
    code: 1137,
    column: 'SL "Chef de Service 35" Zuschlag 25%',
    rate: 43.75,
    isHoursRelated: true,
    isOverTime: true
  },
  {
    code: 1150,
    column: 'SL "Chef de Service 30" Zuschlag 25%',
    rate: 37.5,
    isHoursRelated: true,
    isOverTime: true
  },
  {
    code: 1157,
    column: 'SL "Service 40" Zuschlag 25%',
    rate: 35,
    isHoursRelated: true,
    isOverTime: true
  },
  {
    code: 1158,
    column: 'SL "Service 45" Zuschlag 25%',
    rate: 50,
    isHoursRelated: true,
    isOverTime: true
  },
  { code: 5100, column: 'Reisespesen', isPayrollField: true, payrollFieldId: 233, mutationsCodeAnsatzValue: 0 },
  { code: 8401, column: 'Spesen nach Belegen', isPayrollField: true, payrollFieldId: 235 },
  { code: 8406, column: 'Abzug Diverses', isPayrollField: true, payrollFieldId: 236 },
  { code: 8501, column: 'Hilfs.LOA AZ Einsätze im Monat', isTotalShifts: true, mutationsCodeAnsatzValue: 0 }
];

export const ON_PAY_PAYROLL_CODES = {
  //PREMIER_STAFF_ACCOUNT_ID

  //shiftExportColumns: true for all non hours items

  38454: [
    // Regular hours
    {
      payCode: ({ isRegularWorker }) => (isRegularWorker ? 1 : 300),
      price: ({ payroll }) => payroll?.wage?.hourlyWage || 0,
      qty: ({ payroll }) => payroll?.aggregatedHours?.regular,
      isAggregatePrice: false
    },
    // Overtime
    {
      payCode: ({ isRegularWorker }) => (isRegularWorker ? 2 : 301),
      price: ({ payroll, isRegularWorker }) =>
        payroll?.wage?.hourlyWage * (isRegularWorker ? 1 : payroll?.multipliers?.overtime || 1),
      qty: ({ payroll }) => payroll?.aggregatedHours?.overtime,
      isAggregatePrice: false
    },
    // Overtime 2
    {
      payCode: ({ isRegularWorker }) => (isRegularWorker ? 22 : 302),
      price: ({ payroll, isRegularWorker }) =>
        payroll?.wage?.hourlyWage * (isRegularWorker ? 1 : payroll?.multipliers?.overtime2 || 1),
      qty: ({ payroll }) => payroll?.aggregatedHours?.overtime2,
      isAggregatePrice: false
    },

    // // Travel
    // {
    //   payCode: ({ isRegularWorker }) => (isRegularWorker ? 107 : 303),
    //   price: ({ payroll }) => payroll?.fields?.find(({ id }) => id === PAYROLL_FIELD_TRAVEL_ID)?.value || 0,
    //   qty: 1,
    //   isAggregateQTY: false,
    //   shiftExportColumns: true
    // },

    // Tip
    {
      payCode: ({ isRegularWorker }) => (isRegularWorker ? 208 : 305),
      price: ({ payroll }) => payroll?.fields?.find(({ id }) => id === PAYROLL_FIELD_TIP_ID)?.value || 0,
      qty: 1,
      isAggregateQTY: false,
      shiftExportColumns: true
    },

    // Parking + Travel
    {
      payCode: ({ isRegularWorker }) => (isRegularWorker ? 107 : 303),
      //specific payroll field parking for this account - 657;
      price: ({ payroll }) =>
        //parking
        (payroll?.fields?.find(({ id }) => id === 657)?.value || 0) +
        //travel
        (payroll?.fields?.find(({ id }) => id === PAYROLL_FIELD_TRAVEL_ID)?.value || 0),
      qty: 1,
      isAggregateQTY: false,
      shiftExportColumns: true
    },

    // Commission
    {
      payCode: ({ isRegularWorker }) => (isRegularWorker ? 106 : 309),
      //specific payroll field commission for this account - 706;
      price: ({ payroll }) => payroll?.fields?.find(({ id }) => id === 706)?.value || 0,
      qty: 1,
      isAggregateQTY: false,
      shiftExportColumns: true
    },

    // Salary
    {
      payCode: ({ isRegularWorker }) => (isRegularWorker ? 126 : 308),
      //specific payroll field salary for this account - 716;
      price: ({ payroll }) => payroll?.fields?.find(({ id }) => id === 716)?.value || 0,
      qty: 1,
      isAggregateQTY: false,
      shiftExportColumns: true
    },

    // Training
    {
      payCode: ({ isRegularWorker }) => (isRegularWorker ? 125 : 304),
      //specific payroll field Training for this account - 707;
      price: ({ payroll }) => payroll?.fields?.find(({ id }) => id === 707)?.value || 0,
      qty: 1,
      isAggregateQTY: false,
      shiftExportColumns: true
    }
  ]
};
